import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ClientScheduleRemindersCard } from '@apps/web/components/settings/client_schedule_reminders_card';
import { ShiftConfirmationAutoReplyCard } from '@apps/web/components/settings/shift_confirmation_auto_reply_card';
import { ShiftConfirmationMessagesCard } from '@apps/web/components/settings/shift_confirmation_messages_card';
import { ShiftConfirmationNoResponseWaitCard } from '@apps/web/components/settings/shift_confirmation_no_response_wait_card';
import { ShiftConfirmationRecurringSuppressionToggle } from '@apps/web/components/settings/shift_confirmation_recurring_suppression_toggle';
import { ShiftConfirmationScheduleSection } from '@apps/web/components/settings/shift_confirmation_schedule_section';
import { SegmentedControl } from '@apps/web/components/ui/segmented_control';
import {
    SettingsPageSection,
    SettingsPageSections,
    SettingsPageShell,
    useSettingsGates,
} from '@apps/web/features/settings';

const AUDIENCE_OPTIONS = [
    { value: 'caregivers', label: 'Caregivers' },
    { value: 'clients', label: 'Clients' },
] as const;

type ShiftConfirmationsSearch = {
    readonly audience?: 'caregivers' | 'clients';
};

function readShiftConfirmationsSearch(
    search: Record<string, unknown>,
): ShiftConfirmationsSearch {
    return {
        audience:
            search['audience'] === 'clients'
                ? 'clients'
                : search['audience'] === 'caregivers'
                  ? 'caregivers'
                  : undefined,
    };
}

export const Route = createFileRoute('/_app/settings/shift-confirmations')({
    validateSearch: readShiftConfirmationsSearch,
    component: ShiftConfirmationsPage,
});

function ShiftConfirmationsPage() {
    const { organizationId, gates } = useSettingsGates();
    const search = Route.useSearch();
    const [audience, setAudience] = useState<'caregivers' | 'clients'>(
        () => search.audience ?? 'caregivers',
    );
    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }

    return (
        <SettingsPageShell
            title="Shift confirmation"
            description="Confirm shifts with caregivers, and remind clients who is coming."
        >
            <SegmentedControl
                aria-label="Who to configure"
                options={AUDIENCE_OPTIONS}
                value={audience}
                onValueChange={setAudience}
                layout="auto"
            />
            {audience === 'clients' ? (
                <SettingsPageSections>
                    <ClientScheduleRemindersCard
                        organizationId={organizationId}
                    />
                </SettingsPageSections>
            ) : (
                <SettingsPageSections>
                    <ShiftConfirmationScheduleSection
                        organizationId={organizationId}
                        variant="embedded"
                    />
                    <SettingsPageSection title="Recurring placements">
                        <ShiftConfirmationRecurringSuppressionToggle
                            organizationId={organizationId}
                        />
                    </SettingsPageSection>
                    <SettingsPageSection
                        title="No-response alerts"
                        description="How long Phoebe waits for a caregiver to answer the final follow-up before telling the office there was no response."
                    >
                        <ShiftConfirmationNoResponseWaitCard
                            organizationId={organizationId}
                        />
                    </SettingsPageSection>
                    <SettingsPageSection
                        title="Auto-reply settings"
                        description="Choose which caregiver replies Phoebe can send without manual review."
                    >
                        <ShiftConfirmationAutoReplyCard
                            organizationId={organizationId}
                        />
                    </SettingsPageSection>
                    <SettingsPageSection
                        title="Confirmation templates"
                        description="Edit caregiver-facing shift confirmation templates and preview their dynamic details."
                    >
                        <ShiftConfirmationMessagesCard
                            organizationId={organizationId}
                        />
                    </SettingsPageSection>
                </SettingsPageSections>
            )}
        </SettingsPageShell>
    );
}
